import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { ConnectorSummary } from "@shared/types/connectors";
import type { ConnectorDefinition } from "@/components/connections/types";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { instanceKey } from "@/lib/instanceKey";

/**
 * The connector catalog (FR-30, FR-32).
 *
 * `queryKey[0]` is the literal request path, which is what the default query
 * function in `lib/queryClient.ts` fetches, so these hooks add caching and a
 * type — never a second fetch path. Definitions are rows that change only when
 * an administrator seeds or edits a connector, so they are cached for longer
 * than the 30 second default: re-fetching the sixteen-field shape of a SQL
 * Server form on every focus buys nothing.
 */

const CATALOG_STALE_MS = 5 * 60_000;

export const CONNECTORS_PATH = "/api/v2/connectors";

/**
 * T-51: every connector, each with its `offer` for a new connection in this tenant. The screens
 * that name an existing connection's connector read it (a connector no longer offered keeps its
 * name and logo), and the picker shows only the offered ones from it. The bare path answers only
 * the offered ones, for API callers.
 */
export const CONNECTORS_CATALOG_PATH = `${CONNECTORS_PATH}?include=all`;

export function connectorPath(name: string): string {
  return `${CONNECTORS_PATH}/${encodeURIComponent(name)}`;
}

/** Every active connector, each marked with its `offer` (T-51) for the caller's tenant. */
export function useConnectors(enabled = true): UseQueryResult<ConnectorSummary[]> {
  // The offer depends on the instance's tenant, so the key carries the instance.
  const { selectedInstance } = useTenantInstance();
  return useQuery<ConnectorSummary[]>({
    queryKey: instanceKey(CONNECTORS_CATALOG_PATH, selectedInstance?.id),
    staleTime: CATALOG_STALE_MS,
    enabled,
  });
}

/**
 * A key that cannot collide with a real request path, for the disabled case.
 *
 * A disabled query still READS whatever is cached under its key, so falling
 * back to the catalog path would hand the caller the connector ARRAY typed as
 * one definition — and the first property access on it would blow up a screen
 * that had merely not chosen a connector yet. It must be a key nothing else
 * ever writes.
 */
const NO_CONNECTOR_KEY = "connector-definition:none";

/**
 * One connector's full definition: the field groups, field definitions and
 * entity-selection configuration the form engine renders from (FR-40).
 */
export function useConnectorDefinition(
  name: string | null | undefined,
): UseQueryResult<ConnectorDefinition> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<ConnectorDefinition>({
    queryKey: name ? instanceKey(connectorPath(name), selectedInstance?.id) : [NO_CONNECTOR_KEY],
    staleTime: CATALOG_STALE_MS,
    enabled: Boolean(name),
  });
}
